/**
 * The active info-type list: which info types the Exposed Sensitive Documents page is honouring,
 * and every number, row and chip that follows from it.
 *
 * Split out of `../exposedSensitiveDocuments` because it is a self-contained half of that module:
 * the page's URL selection plus the org's saved default resolve here into one answer, and the
 * rows, the filter bar's controls and its category chips are all read off it. What stays behind
 * is what the rest of the page shares - parsing, the categorisation rule and the category
 * roll-up, the URL codec, counting, sorting, pagination and the freshness labels - which is why
 * this module imports from it and not the other way round.
 *
 * Same rule as its parent: no firebase, no store, no `.svelte`, so it is probed directly under
 * vitest (`test/exposedSensitiveDocumentsInfoTypes.probe.test.ts`).
 */
import {
  byLabelUncategorisedLast,
  categoryOf,
  customInfoTypesByName,
  detectorDisplayName,
  FOLLOW_ORG_DEFAULT,
  rollUpInfoTypes,
  SHOW_ALL_INFO_TYPES,
  type ActiveInfoTypeSelection,
  type ExposedSensitiveDocument,
  type InfoTypeCategoryRollUp,
  type RollUpInfoTypesOptions,
} from "../exposedSensitiveDocuments";

import type { NamedCustomInfoType } from "../categoryLabels";
import type { ExposedDocumentWithStatus } from "./status";

/** The list the page is honouring right now, and which of the three states produced it. */
export interface ActiveInfoTypes {
  kind: "org-default" | "show-all" | "per-user";
  /** The active raw names, or `null` for show-all, which trims nothing at all. */
  infoTypes: string[] | null;
  /**
   * `false` only while the org's saved default is still being read and the answer depends on it.
   * The rows behave as show-all either way - nothing is hidden during the `GET` - but the filter
   * bar's controls must not *claim* show-all, because the reader would be ticking a box whose
   * state is about to change under them.
   */
  settled: boolean;
}

/**
 * The reader's URL selection plus the org's saved default -> the active info-type list.
 *
 * The org default has three values, not two, and the difference matters to the filter bar:
 *
 *  - `undefined` - **not yet fetched**. Unsettled: rows show everything, controls stay quiet.
 *  - `null` - fetched, and there was nothing to read (failed, forbidden, or no document). Settled
 *    show-all: nothing is being hidden and the reader could do nothing about the failure, so the
 *    page says nothing about it (spec R4).
 *  - `string[]` - fetched. An **empty** list resolves to show-all just as an explicit show-all
 *    does (spec 3.3, "absent document or empty list = show all").
 *
 * The `kind` is the resolved state, not the URL state: an org default of `[]` reads as
 * `show-all`, because that is what the reader sees and what the filter bar must reflect. Only the
 * `org-default` URL state can be unsettled - the other two answer without the fetch.
 */
export function resolveActiveInfoTypes(
  selection: ActiveInfoTypeSelection,
  orgDefaultInfoTypes: string[] | null | undefined,
): ActiveInfoTypes {
  if (selection.kind === "show-all") return { kind: "show-all", infoTypes: null, settled: true };
  if (selection.kind === "per-user") return { kind: "per-user", infoTypes: selection.infoTypes, settled: true };
  const settled = orgDefaultInfoTypes !== undefined;
  const orgDefault = orgDefaultInfoTypes ?? [];
  return orgDefault.length === 0
    ? { kind: "show-all", infoTypes: null, settled }
    : { kind: "org-default", infoTypes: orgDefault, settled };
}

/** Which of the filter bar's state controls are on (spec 6.2, filter bar, as amended by #2244). */
export interface InfoTypeFilterControls {
  /** Nothing is being trimmed. The "Show all" button has nothing left to clear. */
  showAll: boolean;
  /** The "Org default active" pill: the selection equals a saved, non-empty org default. */
  isOrgDefault: boolean;
  /**
   * "Back to org default", which drops `?infoTypes` again: shown whenever a saved non-empty
   * default exists and the selection differs from it - a per-user list, and show-all too, because
   * Show all is never the org default and the way back from it has to be on screen.
   */
  canReturnToDefault: boolean;
  /**
   * The `org:write` "Save as org default" button: offered only when the selection is non-empty and
   * differs from the saved default, since saving what is already the default changes nothing.
   * Hidden under show-all: nothing on the page empties the org default.
   */
  canSave: boolean;
  /**
   * `false` while the org's saved default is still in flight (see {@link ActiveInfoTypes.settled}).
   * The bar disables "Show all" and shows no pill and no save until it flips: the rows are
   * show-all in the meantime, but every one of those controls is decided against the default.
   */
  settled: boolean;
}

/** Whether two info-type lists name the same raw names, ignoring order and repeats. */
function sameInfoTypes(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((infoType) => right.has(infoType));
}

/**
 * The resolved active list plus the org's saved default -> the filter bar's controls.
 *
 * "Equals the default" is a set comparison on raw names, so a per-user list that happens to hold
 * exactly the saved default reads as the default: the pill shows and there is nothing to go back
 * to or save. Show-all equals the default only when no non-empty default is saved (an empty list,
 * a failed `GET`, or none at all).
 */
export function infoTypeFilterControls(
  active: ActiveInfoTypes,
  orgDefaultInfoTypes: string[] | null | undefined,
): InfoTypeFilterControls {
  const orgDefault = orgDefaultInfoTypes ?? [];
  const hasDefault = orgDefault.length > 0;
  const showAll = active.kind === "show-all";
  const equalsDefault =
    active.infoTypes === null ? !hasDefault : hasDefault && sameInfoTypes(active.infoTypes, orgDefault);
  const { settled } = active;
  return {
    showAll: settled && showAll,
    isOrgDefault: settled && hasDefault && equalsDefault,
    canReturnToDefault: settled && hasDefault && !equalsDefault,
    canSave: settled && active.infoTypes !== null && active.infoTypes.length > 0 && !equalsDefault,
    settled,
  };
}

/**
 * A chip's × -> the selection to write to the URL.
 *
 * Removing from the org default makes the result a *per-user* list: the reader has departed from
 * the org's default, and the "back to org default" link is how they undo it. So the caller hands
 * in the resolved active names, not the raw URL selection. Show-all never reaches here - it
 * renders no chips, so there is no × to press.
 *
 * **Removing the last chip is show-all, not the org default.** The alternative - dropping
 * `?infoTypes` and springing the org's chips straight back - makes the × look broken, and it is
 * unreachable anyway: from the org default the reader would have to remove every chip to get
 * there, only to be handed all of them again. Show-all is also what {@link decodeInfoTypesParam}
 * already does with a parameter that normalises away to nothing, so the URL
 * `?infoTypes=` round-trips to the same state either way (spec 6.1: empty = show all).
 */
export function removeInfoTypes(active: string[], rawNames: string[]): ActiveInfoTypeSelection {
  const removed = new Set(rawNames);
  const kept = active.filter((infoType) => !removed.has(infoType));
  return kept.length === 0 ? SHOW_ALL_INFO_TYPES : { kind: "per-user", infoTypes: kept };
}

/**
 * A tick in the category search -> the selection to write to the URL (#2247).
 *
 * Applying is always a *per-user* list, even when a tick lands on exactly the org's saved
 * default: the reader has made a choice, and the "back to org default" link is how they give it
 * up again. Applying nothing is show-all, the same ruling as {@link removeInfoTypes} makes for
 * the last chip, so the two ways of emptying the list land in the same place and `?infoTypes=`
 * means one thing.
 */
export function appliedInfoTypes(rawNames: string[]): ActiveInfoTypeSelection {
  return rawNames.length === 0 ? SHOW_ALL_INFO_TYPES : { kind: "per-user", infoTypes: rawNames };
}

/** What the page does with itself once the org default has been written (#2208). */
export interface OrgDefaultSaved {
  /** The page's new copy of the org's saved default: the list the route echoed back. */
  orgDefault: string[];
  /** The URL selection to write - always "follow the org default", never a per-user list. */
  filters: ActiveInfoTypeSelection;
}

/**
 * A successful save of the org default -> what the page adopts (#2208).
 *
 * The **echoed** list is adopted, not the list that was sent: the route de-duplicates and orders
 * before writing, so only the response says what the rest of the org will read. Adopting it
 * locally rather than re-reading means the saver sees that list immediately, with no second `GET`
 * to watch land.
 *
 * `?infoTypes` is dropped at the same time, because the whole point of saving is to make the
 * reader's list the org's: leaving a per-user list in the URL would leave the "back to org
 * default" pill on screen pointing at the very list already on screen.
 *
 * A **failed** save adopts nothing and leaves the URL untouched, so the active list the reader
 * built is not lost. That branch is the absence of this call - the container's `catch` only raises
 * a toast - which is why this function takes the saved list and nothing else.
 */
export function afterOrgDefaultSaved(saved: string[]): OrgDefaultSaved {
  return { orgDefault: [...saved], filters: FOLLOW_ORG_DEFAULT };
}

/**
 * A raw count map restricted to the active list; the whole map under show-all. Membership is
 * tested against the row's own `sdpInfoTypeCounts` keys - raw names, never category keys - so an
 * active name the map has never heard of simply matches nothing (R4: unknown names are harmless).
 */
export function trimInfoTypeCounts(
  sdpInfoTypeCounts: Record<string, number> | undefined,
  active: string[] | null,
): Record<string, number> {
  const counts = sdpInfoTypeCounts ?? {};
  if (active === null) return { ...counts };
  const wanted = new Set(active);
  const trimmed: Record<string, number> = {};
  for (const [infoType, count] of Object.entries(counts)) {
    if (wanted.has(infoType)) trimmed[infoType] = count;
  }
  return trimmed;
}

/** A row plus the two numbers the active list changes, decorated rather than mutated. */
export interface TrimmedExposureRow {
  /** The row as handed in; the page passes rows with their status record joined on. */
  row: ExposedDocumentWithStatus;
  /** `row.sdpInfoTypeCounts` restricted to the active list; the whole map under show-all. */
  infoTypeCounts: Record<string, number>;
  /** The Findings column: the sum over {@link infoTypeCounts}. */
  findings: number;
}

/**
 * The page's one row-visibility rule (spec section 5): a row is shown when at least one key of
 * its `sdpInfoTypeCounts` is in the active list. Show-all is `null` and keeps every row, the same
 * spelling {@link trimRowsToInfoTypes} uses; an **empty list** keeps none, because a caller that
 * reads "empty means show all" resolves that before asking.
 *
 * Exported so anything that counts the documents a list would leave on screen uses this rule, and
 * a second copy of it cannot disagree with the table.
 */
export function rowMatchesInfoTypes(row: ExposedSensitiveDocument, active: string[] | null): boolean {
  if (active === null) return true;
  const wanted = new Set(active);
  return Object.keys(row.sdpInfoTypeCounts).some((infoType) => wanted.has(infoType));
}

/**
 * Trim every row to the active list and drop the rows nothing survives on (spec section 5,
 * row-visibility rule: a row is shown when at least one key of its `sdpInfoTypeCounts` is in the
 * active list).
 *
 * Under show-all every row survives untouched and `findings` is the row's own
 * `sdpTotalFindings`, not the sum of the map: the publisher writes that total and the page shows
 * it verbatim when nothing is trimmed (spec 3.1, 6.2).
 */
export function trimRowsToInfoTypes(rows: ExposedDocumentWithStatus[], active: string[] | null): TrimmedExposureRow[] {
  if (active === null) {
    return rows.map((row) => ({ row, infoTypeCounts: { ...row.sdpInfoTypeCounts }, findings: row.sdpTotalFindings }));
  }
  const trimmed: TrimmedExposureRow[] = [];
  for (const row of rows) {
    if (!rowMatchesInfoTypes(row, active)) continue;
    const infoTypeCounts = trimInfoTypeCounts(row.sdpInfoTypeCounts, active);
    const counts = Object.values(infoTypeCounts);
    trimmed.push({ row, infoTypeCounts, findings: counts.reduce((sum, count) => sum + count, 0) });
  }
  return trimmed;
}

/**
 * The row's info types trimmed to the active list and grouped by category, for the row detail
 * side panel (spec 6.4). The same roll-up the table chips use, so the panel and the row can
 * never disagree; the per-detector breakdown is on each entry's `detectors`.
 */
export function trimmedInfoTypeGroups(
  row: ExposedSensitiveDocument,
  active: string[] | null,
  options: RollUpInfoTypesOptions = {},
): InfoTypeCategoryRollUp[] {
  return rollUpInfoTypes(trimInfoTypeCounts(row.sdpInfoTypeCounts, active), options);
}

/** One chip in the filter bar: a whole category, collapsed up from the active raw names. */
export interface ActiveCategoryChip {
  /** Category key, or {@link UNCATEGORISED_CATEGORY}. */
  category: string;
  label: string;
  /** Set only on an org custom info type's chip, which renders in the custom colour. */
  isCustom?: boolean;
  /** The category's active raw names: every name the selection holds under it, so × drops the whole category. */
  activeInfoTypes: string[];
  /** How those names read (#2250), sorted: the chip's tooltip, which never shows a raw name. */
  displayNames: string[];
  /** k: active raw names in this category. */
  selectedCount: number;
  /** n: candidate raw names in this category, never less than k. */
  candidateCount: number;
  isPartial: boolean;
  /** `3 of 7 types`, set only on a partial chip; a whole chip reads its label alone (spec 6.3, #2244). */
  summary?: string;
}

export interface ActiveCategoryChipOptions {
  /**
   * The raw names the denominator `n` counts: every category's **full membership**, the same set
   * the category search counts its partial tick against (`categoryMembershipOf` in
   * `./categorySearch`), so a chip reads "k of n types" exactly when the search shows that
   * category's tick as indeterminate (#2249). A category ticked in the search is always whole, so
   * a partial chip only comes from a saved default or URL older than today's membership - the
   * stale-default signal spec 6.3 asks for - even when that reads `1 of 68 types`.
   * Omitted, every chip reads as whole.
   */
  candidateInfoTypes?: string[];
  /** The org's enabled custom info types; each is its own category. */
  customInfoTypes?: NamedCustomInfoType[];
}

/**
 * The active raw names, collapsed to one chip per category - never per family, never per raw
 * name (spec 6.3). Sorted alphabetically by label with Uncategorised last, the same order the
 * row chips use.
 *
 * A partial chip is the stale-org-default signal: the saved default predates a change to the
 * info-type map, so only some of the category's names are still active. Its × removes every
 * active name under the category, which takes the whole category out of the selection.
 */
export function activeCategoryChips(active: string[], options: ActiveCategoryChipOptions = {}): ActiveCategoryChip[] {
  const customTypes = customInfoTypesByName(options.customInfoTypes);

  const candidatesByCategory = new Map<string, Set<string>>();
  for (const infoType of options.candidateInfoTypes ?? []) {
    const { category } = categoryOf(infoType, customTypes);
    const names = candidatesByCategory.get(category) ?? new Set<string>();
    names.add(infoType);
    candidatesByCategory.set(category, names);
  }

  const byCategory = new Map<string, ActiveCategoryChip>();
  for (const infoType of new Set(active)) {
    const category = categoryOf(infoType, customTypes);
    const chip = byCategory.get(category.category);
    if (chip) chip.activeInfoTypes.push(infoType);
    else
      byCategory.set(category.category, {
        ...category,
        activeInfoTypes: [infoType],
        displayNames: [],
        selectedCount: 0,
        candidateCount: 0,
        isPartial: false,
      });
  }

  for (const chip of byCategory.values()) {
    chip.activeInfoTypes.sort((a, b) => a.localeCompare(b));
    chip.displayNames = chip.activeInfoTypes
      .map((infoType) => detectorDisplayName(infoType, chip))
      .sort((a, b) => a.localeCompare(b));
    chip.selectedCount = chip.activeInfoTypes.length;
    // An active name the current view knows nothing about still counts towards n, so a chip can
    // never read "3 of 2 types".
    chip.candidateCount = Math.max(candidatesByCategory.get(chip.category)?.size ?? 0, chip.selectedCount);
    chip.isPartial = chip.selectedCount < chip.candidateCount;
    if (chip.isPartial) chip.summary = `${chip.selectedCount} of ${chip.candidateCount} types`;
  }

  return [...byCategory.values()].sort(byLabelUncategorisedLast);
}

/** A chip row cut to the chips that fit, plus what is left over. */
export interface CollapsedChips<T> {
  shown: T[];
  overflow: T[];
  /** `+3 more`, or `""` when nothing overflows. */
  moreLabel: string;
  /** The overflow button's `title`: the hidden chips' labels. `""` when nothing overflows. */
  overflowTitle: string;
}

/** One rendered chip's measured box, the only thing the component contributes to the overflow. */
export interface ChipBox {
  offsetTop: number;
  offsetHeight: number;
}

/** The `gap-1` between chips, so a chip's line height is its own height plus the gap. */
const CHIP_GAP_PX = 4;
/** Sub-pixel slop, so a chip sitting exactly on the line boundary is not counted twice. */
const LINE_SLOP_PX = 2;

/**
 * How many of the rendered chips share the first line.
 *
 * The browser wraps the row; this reads the wrap back off the measured boxes. A chip is on the
 * first line while its top is inside the first line's band, which is the first chip's height
 * plus the flex gap, less a couple of pixels of slop. jsdom reports `offsetTop === 0` for
 * everything, so under vitest this answers "all of them" - which is what the components' tests
 * bypass with an explicit `maxVisible`.
 */
export function chipsOnFirstLine(boxes: ChipBox[]): number {
  if (boxes.length === 0) return 0;
  const lineBottom = boxes[0].offsetTop + boxes[0].offsetHeight + CHIP_GAP_PX - LINE_SLOP_PX;
  return boxes.filter((box) => box.offsetTop < lineBottom).length;
}

/**
 * How many chips to show, given how many fit on the line and how many there are.
 *
 * When they all fit, all of them. When they do not, the `+N more` button needs a slot of its
 * own, so one fitting chip is given back to it - but never the last one: a row of nothing but
 * the button would hide what the reader is filtering by.
 */
export function visibleChipLimit(fitCount: number, total: number): number {
  if (fitCount >= total) return total;
  return Math.max(1, fitCount - 1);
}

/**
 * Cut a chip row to `maxVisible` chips and describe the remainder.
 *
 * The view model cannot measure a line, so the measurement is the component's to make - and the
 * only thing that is: it reads each chip's box, and {@link chipsOnFirstLine} and
 * {@link visibleChipLimit} turn that into the number handed in here. Everything downstream of
 * it - the label, the title, the split - is decided here and probed.
 */
export function collapseChips<T extends { label: string }>(chips: T[], maxVisible: number): CollapsedChips<T> {
  const limit = Number.isFinite(maxVisible) ? Math.max(0, Math.floor(maxVisible)) : chips.length;
  if (chips.length <= limit) return { shown: [...chips], overflow: [], moreLabel: "", overflowTitle: "" };
  const shown = chips.slice(0, limit);
  const overflow = chips.slice(limit);
  return {
    shown,
    overflow,
    moreLabel: `+${overflow.length} more`,
    overflowTitle: overflow.map((chip) => chip.label).join(", "),
  };
}
